import { FC, ReactNode } from 'react';
import { Box, Typography } from '@mui/material';

import styles from './IdeaStopInfo.module.scss';
import { IdeaStop } from '../../../../../../../types/ideaStop.types';
import { IdeaStopField, getIdeaStopFields } from '../../config/ideaStopFieldsConfig';

interface IdeaStopInfoProps {
  ideaStop: IdeaStop;
}

type FieldSegment =
  | { kind: 'group'; groupId: string; fields: IdeaStopField[] }
  | { kind: 'row'; field: IdeaStopField; index: number };

const buildFieldSegments = (fields: IdeaStopField[]): FieldSegment[] => {
  const segments: FieldSegment[] = [];
  let i = 0;
  while (i < fields.length) {
    const f = fields[i];
    const gid = f.fieldGroupId;
    if (gid) {
      const chunk: IdeaStopField[] = [f];
      let j = i + 1;
      while (j < fields.length && fields[j].fieldGroupId === gid) {
        chunk.push(fields[j]);
        j++;
      }
      segments.push({ kind: 'group', groupId: gid, fields: chunk });
      i = j;
    } else {
      segments.push({ kind: 'row', field: f, index: i });
      i++;
    }
  }
  return segments;
};

const isStackedDetailField = (fieldKey: string): boolean => fieldKey === 'description' || fieldKey === 'notes';

const toWebsiteHref = (raw: string): string => {
  const trimmed = raw.trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

const IdeaStopInfo: FC<IdeaStopInfoProps> = ({ ideaStop }) => {
  const fields = getIdeaStopFields(ideaStop.stop_type);

  const formatFieldValue = (field: IdeaStopField, stop: IdeaStop): string => {
    const value = field.getValue(stop);
    if (!value || value.trim() === '') return '';

    try {
      switch (field.type) {
        case 'dateTime':
          return formatDateTime(value);
        case 'date':
          return formatDate(value);
        case 'time':
          return formatTime(value);
        case 'address':
        case 'airport':
        case 'country':
        case 'text':
        default:
          return value;
      }
    } catch (error) {
      console.warn(`Error formatting field ${field.key}:`, error);
      return value;
    }
  };

  const formatDateTime = (dateString: string): string => {
    try {
      const date = new Date(dateString);

      if (isNaN(date.getTime())) {
        return dateString;
      }

      return (
        date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        }) +
        ' ' +
        date.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        })
      );
    } catch (error) {
      console.warn(`Error formatting datetime ${dateString}:`, error);
      return dateString;
    }
  };

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);

      if (isNaN(date.getTime())) {
        return dateString;
      }

      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch (error) {
      console.warn(`Error formatting date ${dateString}:`, error);
      return dateString;
    }
  };

  const formatTime = (dateString: string): string => {
    try {
      const date = new Date(dateString);

      if (isNaN(date.getTime())) {
        return dateString;
      }

      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    } catch (error) {
      console.warn(`Error formatting time ${dateString}:`, error);
      return dateString;
    }
  };

  const fieldsWithValues = fields.filter(field => {
    try {
      const value = field.getValue(ideaStop);
      return value && value.trim() !== '';
    } catch (error) {
      console.warn(`Error getting value for field ${field.key}:`, error);
      return false;
    }
  });

  const descriptionField: IdeaStopField = {
    key: 'description',
    label: 'Description',
    type: 'text',
    sectionDividerAfter: true,
    getValue: (stop: IdeaStop) => stop.description || null,
  };

  const commonFields: IdeaStopField[] = [
    {
      key: 'phone',
      label: 'Phone',
      type: 'text',
      getValue: (stop: IdeaStop) => stop.phone || null,
    },
    {
      key: 'email',
      label: 'Email',
      type: 'text',
      getValue: (stop: IdeaStop) => stop.email || null,
    },
    {
      key: 'link',
      label: 'Website',
      type: 'text',
      getValue: (stop: IdeaStop) => stop.link || null,
    },
  ];

  const allFields: IdeaStopField[] = [];

  try {
    const descValue = descriptionField.getValue(ideaStop);
    if (descValue && descValue.trim() !== '') {
      allFields.push(descriptionField);
    }
  } catch (error) {
    console.warn('Error processing description field:', error);
  }

  allFields.push(...fieldsWithValues);

  commonFields.forEach(commonField => {
    try {
      const value = commonField.getValue(ideaStop);
      if (value && value.trim() !== '') {
        const exists = allFields.some(field => field.key === commonField.key);
        if (!exists) {
          allFields.push(commonField);
        }
      }
    } catch (error) {
      console.warn(`Error processing common field ${commonField.key}:`, error);
    }
  });

  try {
    const notesValue = ideaStop.notes?.trim();
    if (notesValue) {
      allFields.push({
        key: 'notes',
        label: 'Notes',
        type: 'text',
        sectionDividerBefore: true,
        getValue: (stop: IdeaStop) => stop.notes || null,
      });
    }
  } catch (error) {
    console.warn('Error processing notes field:', error);
  }

  const renderFieldValueContent = (field: IdeaStopField, formattedValue: string, stacked: boolean): ReactNode => {
    if (field.key === 'link') {
      return (
        <Typography
          variant="h10Bold"
          component="a"
          href={toWebsiteHref(formattedValue)}
          target="_blank"
          rel="noopener noreferrer"
          className={`${stacked ? styles.valueStacked : styles.value} ${styles.websiteLink}`}
          sx={{ color: '#222B3A' }}
        >
          {formattedValue}
        </Typography>
      );
    }

    if (stacked) {
      return (
        <Typography
          variant="h10"
          component="div"
          className={styles.valueStacked}
        >
          {formattedValue}
        </Typography>
      );
    }

    return (
      <Typography
        variant="h10Bold"
        className={styles.value}
        sx={{ color: '#222B3A' }}
      >
        {formattedValue}
      </Typography>
    );
  };

  const renderFieldRow = (field: IdeaStopField, options?: { showTopDivider?: boolean; showBottomDivider?: boolean }) => {
    try {
      const formattedValue = formatFieldValue(field, ideaStop);

      if (!formattedValue || formattedValue.trim() === '') return null;

      const stacked = isStackedDetailField(field.key);
      const rowClass = `${styles.infoRow} ${stacked ? styles.infoRowStacked : ''} ${
        options?.showTopDivider ? styles.sectionDividerTopRow : ''
      } ${options?.showBottomDivider ? styles.sectionDividerBottomRow : ''}`;

      return (
        <Box
          key={field.key}
          className={rowClass}
        >
          <Typography
            variant="h10"
            className={styles.label}
            sx={{ color: '#222B3A' }}
          >
            {field.label}
          </Typography>
          {renderFieldValueContent(field, formattedValue, stacked)}
        </Box>
      );
    } catch (error) {
      console.warn(`Error rendering field ${field.key}:`, error);
      return null;
    }
  };

  if (allFields.length === 0) {
    return (
      <Box className={styles.ideaStopInfo}>
        <Box className={styles.infoRow}>
          <Typography
            variant="h10"
            className={styles.noDataText}
          >
            No additional details available
          </Typography>
        </Box>
      </Box>
    );
  }

  const segments = buildFieldSegments(allFields);

  return (
    <Box className={styles.ideaStopInfo}>
      {segments.map((seg, segIdx) => {
        const prevSeg = segIdx > 0 ? segments[segIdx - 1] : null;
        const suppressGroupTopBorder =
          seg.kind === 'group' &&
          ((prevSeg?.kind === 'group') ||
            (prevSeg?.kind === 'row' && prevSeg.field.key === 'description'));

        if (seg.kind === 'group') {
          const visibleGroupFields = seg.fields.filter(f => {
            const v = formatFieldValue(f, ideaStop);
            return Boolean(v?.trim());
          });
          if (visibleGroupFields.length === 0) return null;

          return (
            <Box
              key={`${seg.groupId}-${segIdx}`}
              className={`${styles.fieldGroup} ${suppressGroupTopBorder ? styles.fieldGroupNoTop : ''}`}
            >
              {visibleGroupFields.map(field => {
                const formattedValue = formatFieldValue(field, ideaStop);
                if (!formattedValue?.trim()) return null;
                const stacked = isStackedDetailField(field.key);
                return (
                  <Box
                    key={field.key}
                    className={`${styles.infoRow} ${stacked ? styles.infoRowStacked : ''}`}
                  >
                    <Typography
                      variant="h10"
                      className={styles.label}
                      sx={{ color: '#222B3A' }}
                    >
                      {field.label}
                    </Typography>
                    {renderFieldValueContent(field, formattedValue, stacked)}
                  </Box>
                );
              })}
            </Box>
          );
        }

        const field = seg.field;
        const indexInAll = seg.index;
        const suppressTopAfterGroup = prevSeg?.kind === 'group';

        const prevFieldInAll = indexInAll > 0 ? allFields[indexInAll - 1] : undefined;

        const showTopDivider =
          !suppressTopAfterGroup &&
          Boolean(field.sectionDividerBefore) &&
          indexInAll > 0 &&
          !prevFieldInAll?.sectionDividerAfter;

        const showBottomDivider =
          Boolean(field.sectionDividerAfter) && indexInAll < allFields.length - 1;

        return renderFieldRow(field, {
          showTopDivider,
          showBottomDivider,
        });
      })}
    </Box>
  );
};

export default IdeaStopInfo;
